import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Square, RefreshCw, AlertCircle, CheckCircle2, FileImage } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const formatTime = () => {
  const d = new Date();
  return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const VisualStampBackfillPanel = () => {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [running, setRunning] = useState(false);
  const [batchSize, setBatchSize] = useState(80);
  const [logs, setLogs] = useState([]);
  const [totals, setTotals] = useState({ applied: 0, skipped: 0, failed: 0, batches: 0 });
  const stopRequested = useRef(false);
  const logEndRef = useRef(null);

  const appendLog = useCallback((message, type = 'info') => {
    setLogs((prev) => [
      ...prev,
      { id: `${Date.now()}-${prev.length}`, time: formatTime(), message, type },
    ]);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      setStatusError(null);
      const { data } = await api.get('/invoices/admin/backfill-visual-stamps/status', {
        timeout: 120000,
      });
      setStatus({
        pendingSignatures: data.pendingSignatures ?? 0,
        pendingAttachmentFiles: data.pendingAttachmentFiles ?? 0,
        pendingInvoiceFiles: data.pendingInvoiceFiles ?? 0,
        skippedSignatures: data.skippedSignatures ?? 0,
        templateRefreshSignatures: data.templateRefreshSignatures ?? 0,
        templateRefreshAttachmentFiles: data.templateRefreshAttachmentFiles ?? 0,
        templateRefreshInvoiceFiles: data.templateRefreshInvoiceFiles ?? 0,
      });
    } catch (error) {
      const statusCode = error.response?.status;
      let msg = error.response?.data?.error || error.message || 'Nie udało się pobrać statystyk';
      if (statusCode === 403) {
        msg = 'Brak uprawnień (wymagana rola SUPERADMIN).';
      } else if (statusCode === 401) {
        msg = 'Sesja wygasła — zaloguj się ponownie.';
      }
      setStatusError(msg);
      setStatus(null);
      toast.error(msg);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const formatStat = (value) => {
    if (statusLoading) return '…';
    if (statusError) return '—';
    if (value === undefined || value === null) return '—';
    return Number(value).toLocaleString('pl-PL');
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const handleStop = () => {
    stopRequested.current = true;
    appendLog('Zatrzymywanie po bieżącej partii…', 'warn');
  };

  const handleClearLogs = () => {
    setLogs([]);
    setTotals({ applied: 0, skipped: 0, failed: 0, batches: 0 });
  };

  const runMigration = async () => {
    if (running) return;
    stopRequested.current = false;
    setRunning(true);
    appendLog(`Start migracji (partie po ${batchSize} plikach)`, 'info');

    let batchNum = 0;
    let totalApplied = 0;
    let totalSkipped = 0;
    let totalFailed = 0;

    try {
      while (!stopRequested.current) {
        batchNum += 1;
        appendLog(`Partia #${batchNum} — wysyłam żądanie…`, 'info');

        const { data } = await api.post(
          `/invoices/admin/backfill-visual-stamps?limit=${batchSize}`,
          null,
          { timeout: 600000 }
        );

        const applied = data.stampsApplied ?? 0;
        const skipped = data.stampsSkipped ?? 0;
        const failed = data.stampsFailed ?? 0;
        totalApplied += applied;
        totalSkipped += skipped;
        totalFailed += failed;

        setTotals({ applied: totalApplied, skipped: totalSkipped, failed: totalFailed, batches: batchNum });

        appendLog(
          `Partia #${batchNum}: nałożono ${applied}, pominięto (XML/inne) ${skipped}, błędy ${failed} | ` +
            `załączniki ${data.attachmentsProcessed ?? 0} | faktury ${data.invoicesProcessed ?? 0}`,
          failed > 0 ? 'warn' : skipped > 0 && applied === 0 ? 'info' : 'success'
        );

        (data.skipNotes || []).forEach((note) => {
          appendLog(`  ↳ ${note}`, 'info');
        });
        if (skipped > 0 && (data.skipNotes || []).length === 0) {
          appendLog(`  ↳ pominięto ${skipped} (stempel w podglądzie KSeF HTML)`, 'info');
        }

        const failures = data.failures || [];
        failures.slice(0, 8).forEach((f) => {
          appendLog(
            `  ↳ podpis #${f.signatureId}: ${f.message}`,
            'error'
          );
        });
        if (failures.length > 8) {
          appendLog(`  ↳ … i ${failures.length - 8} kolejnych błędów`, 'error');
        }

        await fetchStatus();

        if (!data.hasMore) {
          appendLog('Migracja zakończona — brak kolejnych plików.', 'success');
          toast.success(`Gotowe. Nałożono ${totalApplied} stempli.`);
          break;
        }

        if (stopRequested.current) {
          appendLog('Zatrzymano przez użytkownika.', 'warn');
          toast('Migracja wstrzymana', { icon: '⏸' });
          break;
        }

        appendLog('Kolejna partia za 1 s…', 'info');
        await new Promise((r) => setTimeout(r, 1000));
      }
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Błąd migracji';
      appendLog(`BŁĄD: ${msg}`, 'error');
      toast.error(msg);
    } finally {
      setRunning(false);
      stopRequested.current = false;
      await fetchStatus();
    }
  };

  const pendingFiles =
    (status?.pendingAttachmentFiles ?? 0) + (status?.pendingInvoiceFiles ?? 0);

  const templateRefreshFiles =
    (status?.templateRefreshAttachmentFiles ?? 0) + (status?.templateRefreshInvoiceFiles ?? 0);

  const runTemplateRefresh = async () => {
    if (running) return;
    stopRequested.current = false;
    setRunning(true);
    appendLog(`Start odświeżania szablonu (partie po ${batchSize} plików)`, 'info');

    let batchNum = 0;
    let totalRefreshed = 0;
    let totalFailed = 0;

    try {
      while (!stopRequested.current) {
        batchNum += 1;
        appendLog(`Odświeżanie szablonu — partia #${batchNum}…`, 'info');

        const { data } = await api.post(
          `/invoices/admin/refresh-visual-stamp-template?limit=${batchSize}`,
          null,
          { timeout: 600000 }
        );

        const refreshed = data.stampsApplied ?? 0;
        const failed = data.stampsFailed ?? 0;
        totalRefreshed += refreshed;
        totalFailed += failed;

        setTotals({ applied: totalRefreshed, skipped: 0, failed: totalFailed, batches: batchNum });

        appendLog(
          `Partia #${batchNum}: odświeżono ${refreshed} stempli, błędy ${failed} | ` +
            `załączniki ${data.attachmentsProcessed ?? 0} | faktury ${data.invoicesProcessed ?? 0}`,
          failed > 0 ? 'warn' : 'success'
        );

        (data.skipNotes || []).forEach((note) => appendLog(`  ↳ ${note}`, 'info'));

        await fetchStatus();

        if (!data.hasMore) {
          appendLog('Odświeżanie szablonu zakończone.', 'success');
          toast.success(`Gotowe. Odświeżono ${totalRefreshed} stempli.`);
          break;
        }

        if (stopRequested.current) {
          appendLog('Zatrzymano przez użytkownika.', 'warn');
          break;
        }

        await new Promise((r) => setTimeout(r, 1000));
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Błąd odświeżania';
      appendLog(`BŁĄD: ${msg}`, 'error');
      toast.error(msg);
    } finally {
      setRunning(false);
      stopRequested.current = false;
      await fetchStatus();
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Migracja stempli wizualnych</p>
          <p className="mt-1 text-amber-800">
            Nakłada znaczniki na PDF/skan. Faktury KSeF (XML): próba PDF; jeśli się nie uda —
            stempel zostaje w podglądzie HTML. Załączniki XML KSeF pomijane. Proces może trwać długo —
            możesz zatrzymać po bieżącej partii.
          </p>
        </div>
      </div>

      {statusError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <strong>Nie udało się wczytać statystyk:</strong> {statusError}
          <button
            type="button"
            onClick={fetchStatus}
            className="ml-2 underline font-medium hover:text-red-900"
          >
            Spróbuj ponownie
          </button>
        </div>
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium">Szablon stempla: „podpisany przez” (bez „Elektronicznie”)</p>
          <p className="mt-1 text-blue-800">
            Uruchom odświeżanie, aby poprawić treść na wszystkich dokumentach, które już mają stempel.
            Stare stemple są usuwane z pliku i nanoszone ponownie.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Do nałożenia (PDF/skan)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatStat(status?.pendingSignatures)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Pliki załączników</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatStat(status?.pendingAttachmentFiles)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Pliki faktur (PDF/skan)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatStat(status?.pendingInvoiceFiles)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Pominięte (XML itd.)</p>
          <p className="text-2xl font-bold text-gray-600 mt-1">
            {formatStat(status?.skippedSignatures)}
          </p>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 sm:col-span-2 lg:col-span-4">
          <p className="text-xs text-blue-700 uppercase tracking-wide">Do odświeżenia szablonu (już ze stemplaem)</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {formatStat(status?.templateRefreshSignatures)}{' '}
            <span className="text-sm font-normal text-blue-700">
              ({formatStat(status?.templateRefreshAttachmentFiles)} zał. +{' '}
              {formatStat(status?.templateRefreshInvoiceFiles)} fakt.)
            </span>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="batchSize" className="block text-sm font-medium text-gray-700 mb-1">
            Rozmiar partii (plików)
          </label>
          <select
            id="batchSize"
            value={batchSize}
            onChange={(e) => setBatchSize(Number(e.target.value))}
            disabled={running}
            className="input w-32"
          >
            <option value={20}>20</option>
            <option value={40}>40</option>
            <option value={80}>80</option>
            <option value={120}>120</option>
            <option value={200}>200</option>
          </select>
        </div>

        <button
          type="button"
          onClick={runMigration}
          disabled={running || pendingFiles === 0}
          className="btn-primary inline-flex items-center gap-2"
        >
          <Play className={`h-4 w-4 ${running ? 'opacity-50' : ''}`} />
          {running ? 'Trwa migracja…' : 'Uruchom migrację'}
        </button>

        <button
          type="button"
          onClick={runTemplateRefresh}
          disabled={running || templateRefreshFiles === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Odświeżanie…' : 'Odśwież szablon stempla'}
        </button>

        {running && (
          <button
            type="button"
            onClick={handleStop}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
          >
            <Square className="h-4 w-4" />
            Zatrzymaj
          </button>
        )}

        <button
          type="button"
          onClick={fetchStatus}
          disabled={running || statusLoading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${statusLoading ? 'animate-spin' : ''}`} />
          Odśwież statystyki
        </button>

        <button
          type="button"
          onClick={handleClearLogs}
          disabled={running || logs.length === 0}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Wyczyść logi
        </button>
      </div>

      {(totals.batches > 0 || running) && (
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          <span className="inline-flex items-center gap-1">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            Nałożone w sesji: <strong>{totals.applied}</strong>
          </span>
          <span>
            Pominięte (XML): <strong>{totals.skipped}</strong>
          </span>
          <span>
            Błędy w sesji: <strong className={totals.failed > 0 ? 'text-red-600' : ''}>{totals.failed}</strong>
          </span>
          <span>
            Partie: <strong>{totals.batches}</strong>
          </span>
        </div>
      )}

      <div>
        <div className="flex items-center gap-2 mb-2">
          <FileImage className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-700">Log migracji</h3>
        </div>
        <div
          className="rounded-lg border border-gray-300 bg-gray-900 text-gray-100 font-mono text-xs p-4 h-80 overflow-y-auto"
          role="log"
          aria-live="polite"
        >
          {logs.length === 0 ? (
            <p className="text-gray-500">Brak wpisów — uruchom migrację, aby zobaczyć postęp.</p>
          ) : (
            logs.map((entry) => (
              <div
                key={entry.id}
                className={`mb-1 ${
                  entry.type === 'error'
                    ? 'text-red-400'
                    : entry.type === 'warn'
                      ? 'text-amber-300'
                      : entry.type === 'success'
                        ? 'text-green-400'
                        : 'text-gray-300'
                }`}
              >
                <span className="text-gray-500">[{entry.time}]</span> {entry.message}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
};

export default VisualStampBackfillPanel;
